import {COMMON_BUILDING_TOKENS, SPECIAL_BUILDING_TOKENS} from '../constants/tokens';
import {checkIfCentralQuarter} from '../utils/buildings/checkIfCentralQuarter';
import {checkIfSpecialBuilding} from '../utils/buildings/checkIfSpecialBuilding';

/**
 * СТРОИТЕЛЬСТВО НА ПОЛЕ: жетон все так же падает сверху и выбивает пыль (см.
 * configs/buildingDropAnimation, configs/buildingDustAnimation), а этот конфиг
 * добавляет к падению все остальное. Чем важнее постройка, тем сильнее удар:
 * дорога < обычное строение < квартал < специальное строение < центральный
 * квартал, то есть основание нового города.
 *
 *  - полет (buildBuildingApproachCastPlan): на земле разгорается метка места
 *    падения, за жетоном тянется шлейф, у центрального квартала с неба бьет
 *    столб света цвета игрока;
 *  - удар (buildBuildingImpactCastPlan): вспышка, расходящиеся шестиугольные
 *    волны, раскаленные трещины в земле, каменные осколки и искры, у важных
 *    построек еще лучи, столб света и поднимающиеся блики;
 *  - сам жетон на ударе вспыхивает и пружинит (HexGridRenderer).
 *
 * Квартал еще и прожигает ячейки, на которых теперь нельзя строить другие
 * кварталы (QUARTER_ZONE_BURN, scenes/QuarterZoneBurn).
 *
 * Размеры — в размерах ячейки (hexSize), время — в миллисекундах.
 */

export const BUILDING_IMPACT_KINDS = {
  ROAD: 'road',
  COMMON: 'common',
  QUARTER: 'quarter',
  SPECIAL: 'special',
  CENTRAL: 'central',
};

// Текстуры считает код: utils/effects/buildingImpactTextures
export const BUILDING_IMPACT_TEXTURE_KEYS = {
  CRACKS: 'building_impact_cracks',
  RAYS: 'building_impact_rays',
  BEAM: 'building_impact_beam',
  ZONE_HEAT: 'quarter_zone_heat',
};

const FLIGHT_MS = 390;

export const BUILDING_IMPACTS = {
  [BUILDING_IMPACT_KINDS.ROAD]: {
    flightMs: FLIGHT_MS,
    coverExtraMs: 0,
    approach: {markAlpha: 0.35, streakAlpha: 0.3, streakWidthFactor: 0.4, ghostAlpha: 0.14},
    token: {flashAlpha: 0.5, flashMs: 200, squash: 0.07, settleMs: 200},
    flash: {sizeFactor: 1.5, alpha: 0.55, durationMs: 130},
    glow: {sizeFactor: 2.3, alpha: 0.5, durationMs: 320},
    scorch: {sizeFactor: 1.5, alpha: 0.2, durationMs: 520},
    cracks: null,
    rings: [{startMs: 0, durationMs: 340, reachFactor: 1.75, alpha: 0.7}],
    sparks: {count: 7, reachFactor: {min: 0.9, max: 1.5}, sizeFactor: {min: 0.1, max: 0.15}, lifeMs: {min: 300, max: 480}},
    shards: {count: 4, reachFactor: {min: 0.9, max: 1.4}, sizeFactor: {min: 0.2, max: 0.3}, lifeMs: {min: 340, max: 480}},
    rays: null,
    pillar: null,
    motes: null,
    seal: null,
    shake: null,
  },
  [BUILDING_IMPACT_KINDS.COMMON]: {
    flightMs: FLIGHT_MS,
    coverExtraMs: 0,
    approach: {markAlpha: 0.7, streakAlpha: 0.7, streakWidthFactor: 0.7, ghostAlpha: 0.26},
    token: {flashAlpha: 0.8, flashMs: 260, squash: 0.11, settleMs: 260},
    flash: {sizeFactor: 2, alpha: 0.8, durationMs: 150},
    glow: {sizeFactor: 3, alpha: 0.75, durationMs: 480},
    scorch: {sizeFactor: 1.8, alpha: 0.3, durationMs: 760},
    cracks: {sizeFactor: 2, durationMs: 700},
    rings: [{startMs: 0, durationMs: 420, reachFactor: 2.3, alpha: 0.9}],
    sparks: {count: 16, reachFactor: {min: 1.1, max: 2.1}, sizeFactor: {min: 0.13, max: 0.2}, lifeMs: {min: 360, max: 640}},
    shards: {count: 9, reachFactor: {min: 1.1, max: 2}, sizeFactor: {min: 0.28, max: 0.44}, lifeMs: {min: 400, max: 620}},
    rays: null,
    pillar: null,
    motes: null,
    seal: null,
    shake: null,
  },
  // Волны дотягиваются до границы зоны кварталов: именно они прожигают ячейки
  [BUILDING_IMPACT_KINDS.QUARTER]: {
    flightMs: FLIGHT_MS,
    coverExtraMs: 0,
    approach: {markAlpha: 0.8, streakAlpha: 0.75, streakWidthFactor: 0.75, ghostAlpha: 0.28},
    token: {flashAlpha: 0.9, flashMs: 280, squash: 0.13, settleMs: 280},
    flash: {sizeFactor: 2.3, alpha: 0.85, durationMs: 160},
    glow: {sizeFactor: 3.4, alpha: 0.8, durationMs: 540},
    scorch: {sizeFactor: 2, alpha: 0.34, durationMs: 860},
    cracks: {sizeFactor: 2.3, durationMs: 800},
    rings: [
      {startMs: 0, durationMs: 460, reachFactor: 4.5, alpha: 1},
      {startMs: 90, durationMs: 420, reachFactor: 2.6, alpha: 0.7},
    ],
    sparks: {count: 20, reachFactor: {min: 1.2, max: 2.4}, sizeFactor: {min: 0.14, max: 0.22}, lifeMs: {min: 380, max: 700}},
    shards: {count: 11, reachFactor: {min: 1.2, max: 2.2}, sizeFactor: {min: 0.3, max: 0.48}, lifeMs: {min: 420, max: 660}},
    rays: null,
    pillar: null,
    motes: null,
    seal: null,
    shake: {durationMs: 90, intensity: 0.0012},
  },
  [BUILDING_IMPACT_KINDS.SPECIAL]: {
    flightMs: 460,
    coverExtraMs: 420,
    approach: {markAlpha: 0.9, streakAlpha: 0.85, streakWidthFactor: 0.9, ghostAlpha: 0.32},
    token: {flashAlpha: 1, flashMs: 340, squash: 0.15, settleMs: 340},
    flash: {sizeFactor: 2.8, alpha: 0.95, durationMs: 190},
    glow: {sizeFactor: 4, alpha: 0.9, durationMs: 700},
    scorch: {sizeFactor: 2.2, alpha: 0.36, durationMs: 1000},
    cracks: {sizeFactor: 2.6, durationMs: 960},
    rings: [
      {startMs: 0, durationMs: 500, reachFactor: 3.6, alpha: 1},
      {startMs: 110, durationMs: 520, reachFactor: 2.5, alpha: 0.8},
    ],
    sparks: {count: 28, reachFactor: {min: 1.3, max: 2.8}, sizeFactor: {min: 0.15, max: 0.25}, lifeMs: {min: 420, max: 820}},
    shards: {count: 13, reachFactor: {min: 1.3, max: 2.5}, sizeFactor: {min: 0.32, max: 0.54}, lifeMs: {min: 440, max: 700}},
    rays: {sizeFactor: 4.6, alpha: 0.85, durationMs: 1050, turns: 0.07},
    pillar: {widthFactor: 1.15, heightFactor: 4.6, alpha: 0.72, durationMs: 1050},
    motes: {count: 16, spreadFactor: 0.75, riseFactor: {min: 1.5, max: 2.9}, sizeFactor: {min: 0.09, max: 0.17}, lifeMs: {min: 800, max: 1300}, delayMs: {min: 40, max: 420}},
    seal: null,
    shake: {durationMs: 140, intensity: 0.002},
  },
  // Основание города: столб света с неба, две волны через всю зону, солнце лучей
  // и шестиугольная печать на ячейке
  [BUILDING_IMPACT_KINDS.CENTRAL]: {
    flightMs: 680,
    coverExtraMs: 1050,
    approach: {
      markAlpha: 1,
      streakAlpha: 0.9,
      streakWidthFactor: 1.1,
      ghostAlpha: 0.36,
      beam: {widthFactor: 1.7, heightFactor: 9, alpha: 0.72, fadeInShare: 0.3},
      gather: {count: 18, radiusFactor: 2.3, sizeFactor: {min: 0.1, max: 0.2}, durationShare: 0.7},
    },
    token: {flashAlpha: 1, flashMs: 460, squash: 0.2, settleMs: 460},
    flash: {sizeFactor: 3.6, alpha: 1, durationMs: 240},
    glow: {sizeFactor: 5.4, alpha: 1, durationMs: 1000},
    scorch: {sizeFactor: 2.6, alpha: 0.42, durationMs: 1400},
    cracks: {sizeFactor: 3.1, durationMs: 1300},
    rings: [
      {startMs: 0, durationMs: 560, reachFactor: 5.2, alpha: 1},
      {startMs: 130, durationMs: 620, reachFactor: 4.2, alpha: 0.9},
      {startMs: 300, durationMs: 660, reachFactor: 3, alpha: 0.7},
    ],
    sparks: {count: 42, reachFactor: {min: 1.5, max: 3.6}, sizeFactor: {min: 0.16, max: 0.3}, lifeMs: {min: 480, max: 1000}},
    shards: {count: 20, reachFactor: {min: 1.5, max: 3.2}, sizeFactor: {min: 0.34, max: 0.62}, lifeMs: {min: 480, max: 780}},
    rays: {sizeFactor: 7, alpha: 1, durationMs: 1650, turns: 0.1},
    pillar: {widthFactor: 1.8, heightFactor: 8.5, alpha: 0.85, durationMs: 1600},
    motes: {count: 34, spreadFactor: 1.05, riseFactor: {min: 2, max: 4.4}, sizeFactor: {min: 0.1, max: 0.22}, lifeMs: {min: 1000, max: 1800}, delayMs: {min: 40, max: 760}},
    seal: {durationMs: 1600, alpha: 1, pulseMs: 520},
    shake: {durationMs: 320, intensity: 0.006},
  },
};

// Свечение обычных построек: теплое золото, как у остальных анимаций поля
const WARM_PALETTE = {accent: 0xffa21f, light: 0xfff0b8};
const ROAD_PALETTE = {accent: 0xffc46a, light: 0xfff4d8};

// У каждого специального строения свой цвет света. Гильдии и храмы — как их нити
// влияния (см. configs/glowingThreads): золотой и серебряно-белый
const SPECIAL_BUILDING_PALETTES = {
  [SPECIAL_BUILDING_TOKENS.GUILD]: {accent: 0xffb81f, light: 0xfff2b8},
  [SPECIAL_BUILDING_TOKENS.TEMPLE]: {accent: 0x8fb6ff, light: 0xffffff},
  [SPECIAL_BUILDING_TOKENS.PALACE]: {accent: 0xc77dff, light: 0xf6e6ff},
  [SPECIAL_BUILDING_TOKENS.WAREHOUSE]: {accent: 0xff9a3c, light: 0xffe6c0},
  [SPECIAL_BUILDING_TOKENS.MILITARY_CAMP]: {accent: 0xff4a2e, light: 0xffd8c8},
  [SPECIAL_BUILDING_TOKENS.ARSENAL]: {accent: 0xff7a1f, light: 0xffe0b0},
  [SPECIAL_BUILDING_TOKENS.FORTRESS]: {accent: 0x8fb0dc, light: 0xeef4ff},
};

// Самые темные цвета игроков светлеют: черный свет не читается как свет
const MIN_GLOW_BRIGHTNESS = 70;
const DARK_GLOW_LIFT = 0.42;
const CENTRAL_LIGHT_SHARE = 0.62;

const mixWithWhite = (color, share) => {
  const channel = (shift) => {
    const value = (color >> shift) & 0xff;
    return Math.round(value + (255 - value) * share);
  };
  return (channel(16) << 16) | (channel(8) << 8) | channel(0);
};

/**
 * Какой удар у постройки
 * @param {string} [tokenType]
 * @param {string} [specialBuildingType]
 * @returns {string} BUILDING_IMPACT_KINDS
 */
export const resolveBuildingImpactKind = (tokenType, specialBuildingType) => {
  if (specialBuildingType || checkIfSpecialBuilding(tokenType)) {
    return BUILDING_IMPACT_KINDS.SPECIAL;
  }
  if (checkIfCentralQuarter(tokenType)) {
    return BUILDING_IMPACT_KINDS.CENTRAL;
  }
  if (tokenType === COMMON_BUILDING_TOKENS.QUARTER) {
    return BUILDING_IMPACT_KINDS.QUARTER;
  }
  return tokenType === COMMON_BUILDING_TOKENS.ROAD ? BUILDING_IMPACT_KINDS.ROAD : BUILDING_IMPACT_KINDS.COMMON;
};

/**
 * Цвет света постройки
 * @param {string} kind — BUILDING_IMPACT_KINDS
 * @param {Object} [params]
 * @param {string} [params.buildingType] — какое специальное строение
 * @param {number} [params.playerColor] — цвет игрока: им светится основание города
 * @returns {{accent: number, light: number}}
 */
export const resolveBuildingImpactPalette = (kind, {buildingType, playerColor} = {}) => {
  if (kind === BUILDING_IMPACT_KINDS.CENTRAL && Number.isFinite(playerColor)) {
    const brightness = ((playerColor >> 16) & 0xff) * 0.3 + ((playerColor >> 8) & 0xff) * 0.59 + (playerColor & 0xff) * 0.11;
    const accent = brightness < MIN_GLOW_BRIGHTNESS ? mixWithWhite(playerColor, DARK_GLOW_LIFT) : playerColor;
    return {accent, light: mixWithWhite(accent, CENTRAL_LIGHT_SHARE)};
  }
  if (kind === BUILDING_IMPACT_KINDS.SPECIAL) {
    return SPECIAL_BUILDING_PALETTES[buildingType] || WARM_PALETTE;
  }
  return kind === BUILDING_IMPACT_KINDS.ROAD ? ROAD_PALETTE : WARM_PALETTE;
};

/**
 * Сколько двойник жетона держится над полем после удара сверх времени пыли: пока
 * лучи и столб света за ним не погаснут, иначе они на миг легли бы поверх жетона
 */
export const resolveBuildingDropCoverExtraMs = (kind) => BUILDING_IMPACTS[kind]?.coverExtraMs || 0;

export const BUILDING_IMPACT_COLORS = {
  white: 0xffffff,
  scorch: 0x1d120a,
  crackHot: 0xfff0b0,
  crackWarm: 0xff8a1f,
  crackCold: 0x4a1a08,
};

export const BUILDING_IMPACT_FLIGHT = {
  // Метка на земле стягивается к контуру ячейки
  markFromScale: 1.5,
  markSizeFactor: 1.3,
  // Длина шлейфа за жетоном на самом быстром участке
  streakLengthFactor: 3.4,
  // Отставание призраков жетона от него самого
  ghostLagsMs: [28, 58, 92],
};

export const BUILDING_IMPACT_LIMITS = {
  // Применение плана ставит пачку жетонов подряд: сверх этого новые удары идут
  // без осколков, искр и лучей, остаются вспышка и волна
  maxFullImpacts: 3,
};

/**
 * ПРОЖИГАНИЕ ЗОНЫ КВАРТАЛОВ. Ячейка, на которой из-за нового квартала больше
 * нельзя строить другие кварталы, вспыхивает, когда до нее докатывается волна
 * удара: штриховка раскаляется добела, ячейку заливает жар, и все это остывает
 * до обычной штриховки зоны.
 */
export const QUARTER_ZONE_BURN = {
  // Задержка вспышки на шаг ячейки от квартала: волна идет наружу
  stepDelayMs: 95,
  igniteMs: 110,
  // Жар в ячейке
  heat: {alpha: 0.9, fadeFromMs: 280, endMs: 1500},
  // Свечение контура ячейки
  outline: {sizeFactor: 1.1, alpha: 1, endMs: 1050},
  // Раскаленная штриховка: держится, потом растворяется, открывая обычную
  hatch: {holdMs: 900, endMs: 2100},
  // Обычная штриховка проявляется под раскаленной
  base: {startMs: 500, endMs: 1700},
  // Жар остывает: белый → желтый → оранжевый → темно-красный
  ramp: [
    {atMs: 0, color: 0xffffff},
    {atMs: 170, color: 0xffe27a},
    {atMs: 520, color: 0xff8a1f},
    {atMs: 1150, color: 0xd8380f},
    {atMs: 2100, color: 0x7a1e08},
  ],
  // Угольки над ячейкой
  embers: {count: 3, sizeFactor: 0.15, spreadFactor: 0.62, riseFactor: 0.95, startMs: 60, lifeMs: 780, staggerMs: 170, tints: [0xffd27a, 0xff8a1f, 0xfff0b0]},
};

export const QUARTER_ZONE_BURN_TOTAL_MS = QUARTER_ZONE_BURN.hatch.endMs;

// Чужие стартовые кварталы при раскрытии падают не разом, а друг за другом: так видно
// каждый, и на телефоне удары не складываются в один кадр
export const STARTING_QUARTERS_REVEAL_STAGGER_MS = 350;

// Звук основания города (см. configs/deferredSounds, SoundManager)
export const CITY_FOUNDED_SOUND_ID = 'building_city_founded_horn';
